import { useEffect, useMemo, useState } from 'react';
import { getUserInfo, getStorage, STORAGE_KEYS } from '@shared/api/client';
import { getMyProfile } from '@features/profile/api/profileService';
import { getPostcardItems, createPostcard } from '../api/postcardService';
import PostcardCanvas from './PostcardCanvas';
import PostcardViewModal from './PostcardViewModal';
import { ROUTES } from '@app/router/routes';
import CloseButton from '@/shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

const MAX_FROM_NAME = 30;
const MAX_CONTENT = 100;

function buildFromOptions({ name, dharma, temple }) {
  const opts = [];
  const t = temple?.trim() && temple.trim() !== '無' ? temple.trim() : '';
  const n = name?.trim() || '';
  const d = dharma?.trim() || '';
  if (n) opts.push({ value: n, label: n });
  if (d) opts.push({ value: d, label: d });
  if (t && n) opts.push({ value: `${t}‧${n}`, label: `${t}‧${n}` });
  if (t && d) opts.push({ value: `${t}‧${d}`, label: `${t}‧${d}` });
  if (d && n) opts.push({ value: `${d}‧${n}`, label: `${d}‧${n}` });
  return opts;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function PostcardCreateModal({ isOpen, onClose }) {
  const user = getUserInfo();
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const [categories, setCategories] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');

  const [selectedItem, setSelectedItem] = useState(null);
  const [toName, setToName] = useState('');
  const [content, setContent] = useState('');

  const [fromRealName, setFromRealName] = useState(user?.name ?? '');
  const [fromDharma, setFromDharma] = useState(user?.dharmaName ?? '');
  const [fromTemple, setFromTemple] = useState(user?.monasteryName ?? '');
  const [fromName, setFromName] = useState(user?.name ?? user?.dharmaName ?? '');

  const fromOptions = useMemo(
    () => buildFromOptions({ name: fromRealName, dharma: fromDharma, temple: fromTemple }),
    [fromRealName, fromDharma, fromTemple]
  );

  useEffect(() => {
    if (fromOptions.length > 0 && !fromOptions.find(o => o.value === fromName)) {
      setFromName(fromOptions[0].value);
    }
  }, [fromOptions]);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdResult, setCreatedResult] = useState(null);

  useEffect(() => {
    if (!isOpen || !getStorage(STORAGE_KEYS.MEMBER_TOKEN)) return;
    getMyProfile().then(profile => {
      if (!profile) return;
      setFromRealName(profile.user_name ?? fromRealName);
      setFromDharma(profile.dharma_name ?? fromDharma);
      setFromTemple(profile.monastery_name ?? fromTemple);
    }).catch(() => { });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setItemsLoading(true);
    getPostcardItems()
      .then(setCategories)
      .catch(() => setItemsError(t('postcard-create.itemsLoadError')))
      .finally(() => setItemsLoading(false));
  }, [isOpen]);

  // 開啟時鎖住背景 scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  function handleClose() {
    setStep(1);
    setSelectedItem(null);
    setToName('');
    setContent('');
    setError('');
    setCreatedResult(null);
    window.history.pushState({}, '', '/');
    onClose();
  }

  async function handleSubmit() {
    if (!toName.trim() || !content.trim() || !fromName.trim()) {
      setError(t('postcard-create.requiredFields'));
      return;
    }
    if (!selectedItem) { setError(t('postcard-create.selectDesignFirst')); return; }
    if (!user) { window.location.href = ROUTES.LOGIN; return; }
    setLoading(true);
    setError('');
    try {
      const res = await createPostcard({
        item_id: selectedItem.item_id,
        content: content.trim(),
        from_name: fromName.trim(),
        to_name: toName.trim(),
      });
      setCreatedResult(res);
      setStep(3);
      const code = res.share_code ?? res.record_id;
      if (code) window.history.pushState({}, '', `/card/${code}`);
    } catch (res) {
      setError(res.message || t('postcard-create.createFailed'));
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  // 建立成功後切到 ViewModal
  if (createdResult) {
    return (
      <PostcardViewModal
        isOpen={true}
        onClose={handleClose}
        shareCode={createdResult.share_code}
        recordId={createdResult.record_id}
        openShare={true}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-[#FAF7F3] flex flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="flex-shrink-0 bg-gradient-to-b from-[#F5EFE6] to-[#FAF7F3] border-b border-[#E8DCC8] px-4 py-8 text-center relative">
        <CloseButton btnColor="text-[#B39029]" onClick={handleClose} />
        <p className="text-xs tracking-[0.3em] text-[#B39029] mb-2 uppercase">35th Anniversary</p>
        <div className="flex justify-center items-center gap-3 mt-6">
          {[t('postcard.stepSelectDesign'), t('postcard.stepFillContent'), t('postcard.share')].map((label, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                onClick={() => i < step - 1 && setStep(i + 1)}
                className={`flex items-center gap-1.5 ${i < step - 1 ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === i + 1 ? 'bg-[#907654] text-white shadow-md scale-110'
                  : step > i + 1 ? 'bg-[#B39029] text-white'
                    : 'bg-[#E8DCC8] text-[#6E625B]'
                  }`}>
                  {step > i + 1 ? (
                    <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : i + 1}
                </span>
                <span className={`text-sm font-medium ${step === i + 1 ? 'text-[#907654]' : step > i + 1 ? 'text-[#B39029]' : 'text-[#6E625B] opacity-50'
                  }`}>{label}</span>
              </button>
              {i < 2 && <div className={`w-6 h-px ${step > i + 1 ? 'bg-[#B39029]' : 'bg-[#E8DCC8]'}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Step 1 */}
        {step === 1 && (
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#F0EAE4] space-y-5">
              {itemsLoading && (
                <div className="flex justify-center py-10">
                  <div className="w-8 h-8 border-2 border-[#907654] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {itemsError && <div className="text-sm text-red-500 text-center py-4">{itemsError}</div>}
              {!itemsLoading && !itemsError && categories.map((cat) => (
                <div key={cat.category}>
                  <p className="text-base font-semibold text-[#6E625B] mb-3 tracking-wider">{cat.category}</p>
                  <div className="grid gap-3" style={{ gridTemplateColumns: isMobile ? (cat.items?.[0]?.is_full_bleed ? 'repeat(2, 1fr)' : '1fr') : 'repeat(4, 1fr)' }}>
                    {(cat.items ?? []).map((item) => {
                      const isSelected = selectedItem?.item_id === item.item_id;
                      const thumbUrl = isMobile ? item.mobile_url : item.desktop_url;
                      const isMascot = !item.is_full_bleed;
                      return (
                        <button
                          key={item.item_id}
                          onClick={() => { setSelectedItem(item); setStep(2); }}
                          className={`relative rounded-xl overflow-hidden cursor-pointer transition-all group ${isSelected
                            ? 'outline outline-[3px] outline-[#C0392B] outline-offset-2 shadow-[0_0_0_6px_rgba(192,57,43,0.15)] scale-[1.03]'
                            : 'outline outline-2 outline-[#E8DCC8] hover:outline-[#C0392B]/50'
                            }`}
                          style={{ aspectRatio: isMobile ? (isMascot ? '69/44' : '1/1') : (isMascot ? '600/383' : '16/9') }}
                        >
                          <img src={thumbUrl} alt={item.name} className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${isMascot ? 'object-contain' : 'object-cover'}`} />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent py-2 px-2">
                            <span className="text-white text-xs font-medium drop-shadow">{item.name}</span>
                          </div>
                          {isSelected && (
                            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#C0392B] shadow flex items-center justify-center">
                              <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="px-4 py-6 space-y-4">
            {!isMobile && (
              <div className="max-w-5xl mx-auto space-y-3">
                <PostcardCanvas
                  item={selectedItem} fromName={fromName} toName={toName} content={content}
                  isMobile={false} editable={true}
                  onToName={setToName} onContent={setContent} onFromName={setFromName}
                  fromOptions={fromOptions} maxContent={MAX_CONTENT} maxName={MAX_FROM_NAME}
                />
              </div>
            )}
            {isMobile && (
              <div className="-mx-4">
                <PostcardCanvas
                  item={selectedItem} fromName={fromName} toName={toName} content={content}
                  isMobile={true} editable={true}
                  onToName={setToName} onContent={setContent} onFromName={setFromName}
                  fromOptions={fromOptions} maxContent={MAX_CONTENT} maxName={MAX_FROM_NAME}
                />
              </div>
            )}
            {error && (
              <div className={`bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-600 flex items-center gap-2 ${isMobile ? 'max-w-sm mx-auto' : 'max-w-5xl mx-auto'}`}>
                <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            <div className={`flex gap-3 ${isMobile ? 'max-w-sm mx-auto' : 'max-w-5xl mx-auto'}`}>
              <button
                onClick={() => setStep(1)}
                className="flex-1 border border-[#E8DCC8] text-[#6E625B] rounded-xl py-3 text-sm font-bold cursor-pointer hover:bg-[#FAF7F3] transition-colors"
              >
                {t('postcard-create.prevStep')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-[#B39029] hover:bg-[#9a7a22] disabled:opacity-50 text-white rounded-xl py-3 text-sm font-bold cursor-pointer transition-colors shadow-md"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{t('postcard-create.creating')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    {t('postcard.share')}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
